import React from 'react';
import { useMfaChallenge, type MfaChallengeError, type Resolver } from '../../hooks/useMfaChallenge';
import { Button } from '../index';

/**
 * The SMS challenge screen — CU-86ajwpnek §7.
 *
 * ⚠️ Rendered INSTEAD OF the sign-in form, never stacked over it. Leaving the
 * password step mounted behind this invites a second submit against a resolver
 * that is already pending.
 *
 * ── INVARIANTS ────────────────────────────────────────────────────────────
 * • The masked number comes from Identity Platform and is displayed VERBATIM.
 *   Never unmask, reconstruct, or echo it back (M3).
 * • Copy carries no error codes, no "IdP", no "resolver". A patient cannot act
 *   on any of those.
 * • The patient is NEVER trapped: cancelling returns to sign-in, where their
 *   password still works as the durable root.
 */

const RECAPTCHA_CONTAINER_ID = 'mfa-challenge-recaptcha';

function errorCopy(error: MfaChallengeError): string {
  switch (error.kind) {
    case 'bad-code':
      return "That code didn't match. Please check it and try again.";
    case 'session-expired':
      // Points at the cheaper remedy that is already on screen, rather than
      // sending the patient back to the password step.
      return 'That code has expired. Choose "Send a new code" to get a fresh one.';
    case 'too-many-requests':
      // Deliberately NOT "try again in a moment" — this cooldown is measured in
      // hours, and a wrong timeframe sends the patient to support angry.
      return 'Too many attempts. For security, please wait a while before requesting another code.';
    case 'send-failed':
      return "We couldn't send your code just now. Please try again in a moment.";
    case 'resend-cap-reached':
      // Ours, not IdP's, and recoverable RIGHT NOW by starting over — so the
      // copy says so rather than implying a wait.
      //
      // ⚠️ "Start over", NOT "sign in again" (CU-86ajtgauu). This form is also
      // mounted for ALREADY-SIGNED-IN patients (PasswordUpdate,
      // PasskeyEnrollmentSection); the old wording was harmless only because
      // the cap copy was unreachable. Same wording as the enrollment section.
      return "We've sent several codes already. Please start over, or contact info@mideohealth.com if the messages aren't arriving.";
    case 'offline':
      // CU-86akbg4qm — the one failure a patient can always fix themselves, so
      // no support address and no timeframe. ⚠️ NOT mobile's copy: "Your
      // information is safe" earned its place at the enrollment gate and would
      // invite worry here. Reads correctly on every mount of this form,
      // signed-in or not.
      return "We couldn't reach MIDEO. Please check your internet connection and try again.";
    case 'generic':
    default:
      return 'Something went wrong. Please try again in a moment, or contact info@mideohealth.com if it keeps happening.';
  }
}

interface MfaChallengeFormProps {
  resolver: Resolver;
  onComplete?: () => void;
  onCancel?: () => void;
  /**
   * The email typed at the password step, for Sentry correlation
   * (CU-86akbcqv8).
   *
   * ⚠️ A CLAIM, never a verified identity — never rendered, never echoed to the
   * patient, never `Sentry.setUser`. See `useMfaChallenge`'s parameter doc and
   * `CLAUDE/docs/guides/sentry-phi-determinations.md`.
   */
  claimedEmail?: string;
}

export function MfaChallengeForm({ resolver, onComplete, onCancel, claimedEmail }: MfaChallengeFormProps) {
  const [code, setCode] = React.useState('');
  const sent = React.useRef(false);

  const { step, busy, error, maskedPhone, sendCode, submitCode, clearError, resendCooldownSecs, resendCapReached } =
    useMfaChallenge(resolver, RECAPTCHA_CONTAINER_ID, {}, claimedEmail);

  // Auto-dispatch on mount. The patient has already proven their password;
  // making them click "send me a code" is a step with no decision in it.
  //
  // ⚠️ The ref guard matters: a second OTP per render would burn IdP's rate
  // limiter and confuse which code is live. Dispatched from an EFFECT rather
  // than during render, because the reCAPTCHA container must be in the DOM
  // first.
  React.useEffect(() => {
    if (!resolver || sent.current) return;
    sent.current = true;
    void sendCode();
  }, [resolver, sendCode]);

  /**
   * FIRE `onComplete` EXACTLY ONCE PER CHALLENGE — same ref-guard pattern as
   * `sent` above, and for the same reason.
   *
   * ⚠️ MEASURED 2026-08-12 (CU-86ajzr8x1). The unguarded form of this effect
   * (`if (step === 'done') onComplete?.()` with deps `[step, onComplete]`) fires
   * MORE THAN ONCE. Both consumers pass `onComplete` as an INLINE ARROW that
   * sets parent state — login.tsx:333 and enroll.tsx:125 — so the callback has a
   * new identity on every render, it sits in this effect's dependency array, and
   * the callback itself causes the re-render that re-runs it while `step` is
   * still 'done'. Measured counts, against this component:
   *
   *     consumer that nulls the resolver (unmounts)      → 1
   *     ...the same, under StrictMode                    → 2   ⚠️ TODAY
   *     consumer that stays mounted                      → 2
   *     ...the same, under StrictMode                    → 3
   *     a STABLE callback identity                       → 1   (the cause)
   *
   * ⚠️ THIS IS A PRESENT-TENSE BUG, NOT A LATENT ONE. It is often described as
   * "masked by unmount timing" because both consumers null the resolver on
   * completion. That masking holds only WITHOUT StrictMode — row 2 above is the
   * shipped login path double-firing today. What was latent is only the
   * consequence: a consumer whose `onComplete` does real work rather than
   * navigating. CU-86ajzr8x1 adds one (passkey enrollment), where a second call
   * means a second WebAuthn sheet and a second credential against the
   * 5-credential cap.
   *
   * ⚠️ GUARDED HERE RATHER THAN ONLY IN THE CALLER, because this component is
   * shared and a shared component cannot assume every caller's callback is
   * idempotent. Depth belongs at the source of the duplication.
   *
   * ⚠️ KEYED ON THE RESOLVER, NOT A BARE BOOLEAN — AND REPORTED HONESTLY AS
   * DEFENCE IN DEPTH RATHER THAN AS A LIVE FIX. Mutation-tested 2026-08-12:
   * degrading this to `if (fired.current) return; fired.current = true` changes
   * NO test outcome, and that is a true statement about this repo rather than a
   * gap in the tests. Both consumers render this form CONDITIONALLY (early
   * `if (mfaResolver)` at login.tsx:323 and enroll.tsx:115), so cancel → retry
   * UNMOUNTS it and a fresh instance gets a fresh ref — a bare boolean would
   * reset anyway. There is currently no path that swaps the resolver on a LIVE
   * instance.
   *
   * The keying is kept regardless, and is NOT to be "simplified" away: it costs
   * one comparison, and sub-task 4 (CU-86ajzr9fp) turns the MFA gate into a
   * factor CHOICE where `onCancel` returns to the choice rather than dismissing
   * — plausibly the first consumer that keeps this form mounted and swaps the
   * prop. With a bare boolean, that consumer would silently get a dead
   * "Sign in" button on the second challenge.
   *
   * Three details are load-bearing: the early return happens BEFORE the ref is
   * touched (so it re-arms per challenge); the ref stores resolver IDENTITY; and
   * it is set BEFORE `onComplete?.()` (so a synchronous re-render caused by the
   * callback finds it already set).
   */
  const completedFor = React.useRef<Resolver | null>(null);
  React.useEffect(() => {
    if (step !== 'done') return;
    if (completedFor.current === resolver) return;
    completedFor.current = resolver;
    onComplete?.();
  }, [step, onComplete, resolver]);

  const resendLabel = resendCapReached
    ? 'No more codes available'
    : resendCooldownSecs > 0
      ? `Send a new code in ${resendCooldownSecs}s`
      : 'Send a new code';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || code.trim().length === 0) return;
    void submitCode(code.trim());
  };

  const handleResend = () => {
    // Clear the typed code too — it belongs to the superseded OTP. Except AT
    // the cap: no new code is coming, so the typed one may be their best chance.
    if (!resendCapReached) setCode('');
    clearError();
    void sendCode();
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      <h2 className="text-xl font-semibold">Check your phone</h2>
      <p className="text-sm">
        {maskedPhone
          ? `We've sent a code to ${maskedPhone}. Enter it below to finish signing in.`
          : "We've sent a code to your mobile number. Enter it below to finish signing in."}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label htmlFor="mfa-code" className="text-sm font-medium">
          Verification code
        </label>
        <input
          id="mfa-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            // Without this the rejection reads as frozen while a corrected code
            // is typed, and the patient concludes the new code failed too
            // (measured on mobile 2026-07-29).
            if (error) clearError();
          }}
          placeholder="6-digit code"
          inputMode="numeric"
          // The web equivalent of mobile's SMS autofill.
          autoComplete="one-time-code"
          aria-invalid={!!error}
          aria-describedby={error ? 'mfa-code-error' : undefined}
          className="rounded border px-3 py-2"
          disabled={busy}
        />

        {error && (
          <p id="mfa-code-error" role="alert" className="text-sm text-red-700">
            {errorCopy(error)}
          </p>
        )}

        <Button
          id="mfa-submit"
          type="submit"
          onClick={handleSubmit}
          textContent="Sign in"
          disabled={busy || code.trim().length === 0}
          working={busy}
          workingText="Checking..."
        />
      </form>

      {/* A disabled button with no explanation reads as broken, so the label
          always says why it cannot be pressed. */}
      <button
        type="button"
        onClick={handleResend}
        // ⚠️ NOT disabled at the cap (CU-86ajtgauu). The hook's cap branch —
        // the Sentry signal and the "start over" copy — is reached ONLY by this
        // press. Nor gated on the cooldown once capped: the 5th send arms 120s,
        // which would leave "No more codes available" dead for two minutes.
        disabled={busy || (!resendCapReached && resendCooldownSecs > 0)}
        className="text-sm underline disabled:no-underline disabled:opacity-60"
      >
        {resendLabel}
      </button>

      <button type="button" onClick={onCancel} className="text-sm underline">
        Back to sign in
      </button>

      {/* ⚠️ Must be in the DOM before the verifier is constructed, and must
          never be conditionally rendered — the hook builds the verifier against
          this id on mount. */}
      <div id={`${RECAPTCHA_CONTAINER_ID}-host`} />
    </div>
  );
}

export default MfaChallengeForm;
